import React from 'react';
import PropTypes from 'prop-types';
import { Dialog, Box, Typography, makeStyles, Grid, useTheme } from '@material-ui/core';
import { PrimaryButton } from '../commonUtilities/GenericButtonElements';
import { styleBreakDesktop } from '../layouts/Globals';
import { getCustomColors } from '../../../styles/CustomColors';
import clsx from 'clsx';
import { ReactComponent as Cancel } from '../../../resources/images/close-small.svg';

const Colors = getCustomColors();

const useStyles = makeStyles(theme => ({
    dialogBox: {
        padding: theme.spacing(3),
        maxWidth: '368px',
        [styleBreakDesktop(theme)]: {
            maxWidth: '473px',
            width: '473px',
        },
    },
    dialogText: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(3),
    },
    dialogText2: {
        marginTop: '-12px',
        marginBottom: theme.spacing(3),
    },
    dialogContainer: {
        padding: theme.spacing(0),
        [styleBreakDesktop(theme)]: {
            justifyContent: 'flex-end',
        },
    },
    emptyItem: {
        display: 'none',
    },
    dialogButton: {
        width: '100%',
        [styleBreakDesktop(theme)]: {
            minWidth: '145px',
        },
    },
    dialogGridItem: {
        display: 'flex',
        justifyContent: 'space-between',
    },
    dialogButtonFullWidth: {
        width: '100%',
    },
    dialogPaper: {
        marginLeft: theme.spacing(0),
        marginRight: theme.spacing(0),
    },
    dialogBackdrop: {
        backgroundColor: Colors.BACKGROUND_COLOUR_WITH_TRANSPARENCY,
    },
    dialogPaperWidthFalse: {
        maxWidth: 'calc(100% - 32px)',
    },
    cancel: {
        cursor: 'pointer',
    },
}));

/**
 * An alert modal that displays Yes and No.
 * If we get another AlertModal type here, maybe something like AlertModalOk, we could then extract an AlertModal base.
 *
 * @param onCloseSelected Handler to call when user dismisses the dialog (by clicking outside).
 *
 * @param onNoSelected Optional. Handler when user clicks no. If not provided, no Negative button is displayed.
 * @param noButtonText Optional. Text to display on the No button. Only required if onNoSelected is also provided. Default is 'No'.
 *
 * @param onYesSelected Handler when user clicks yes.
 * @param yesButtonText Optional. Text to display on the yes button. Default is 'Yes'.
 */
const AlertModalYesNo = ({
    isDisplayed,
    alertModalTitle,
    alertModalBody,
    alertModalBody2,
    onNoSelected,
    onYesSelected,
    onCloseSelected,
    noButtonText = 'No',
    yesButtonText = 'Yes',
    datatestid,
    noButtonCustomStyle,
    yesButtonCustomStyle,
    noButtonClasses,
    yesButtonClasses,
    dataCyNoButton,
    dataCyYesButton,
    onCancelSelected,
    dataCyAlertModalTitle,
    dataCyAlertModalBody,
    dataCyAlertModalBody2,
}) => {
    const classes = useStyles();
    const theme = useTheme();
    return (
        // using an inline style for z-index to override the default inline style value on Dialog.
        // Need a much higher value so that it can overlay on any html element. The default value is 'z-index: 1300',
        // which is not high enough for certain elements such as the footer and header on stepsLayout
        <Dialog
            open={isDisplayed}
            onClose={onCloseSelected}
            maxWidth={false}
            style={{ zIndex: 10000 }}
            classes={{
                paper: classes.dialogPaper,
                paperWidthFalse: classes.dialogPaperWidthFalse,
            }}
            BackdropProps={{
                classes: {
                    root: classes.dialogBackdrop,
                },
            }}
        >
            <Box component="div" display="flex" flexDirection="column" className={classes.dialogBox}>
                <Box display="flex" justifyContent="space-between">
                    <Typography variant="h2" data-cy={dataCyAlertModalTitle ?? ''}>
                        {alertModalTitle}
                    </Typography>
                    {onCancelSelected && (
                        <Box>
                            <Cancel aria-labelledby="cancel" fill={theme.palette.primary.dark} onClick={onCancelSelected} className={classes.cancel} />
                        </Box>
                    )}
                </Box>
                <Typography component="div" variant="body1" className={classes.dialogText} data-cy={dataCyAlertModalBody ?? ''}>
                    {alertModalBody}
                </Typography>
                {alertModalBody2 && (
                    <Typography component="div" variant="body1" className={classes.dialogText2} data-cy={dataCyAlertModalBody2 ?? ''}>
                        {alertModalBody2}
                    </Typography>
                )}
                <Grid container spacing={2} className={classes.dialogContainer}>
                    {/* If there is only a Yes button, we want the button to fill the space. */}
                    {onNoSelected && <Grid item xs className={classes.emptyItem}></Grid>}
                    {onNoSelected && (
                        <Grid item xs className={classes.dialogGridItem}>
                            <PrimaryButton
                                variant="outlined"
                                onClick={onNoSelected}
                                className={clsx(classes.dialogButton, {
                                    [noButtonCustomStyle]: !!noButtonCustomStyle,
                                })}
                                classes={noButtonClasses ?? {}}
                                data-cy={dataCyNoButton ?? ''}
                            >
                                {noButtonText}
                            </PrimaryButton>
                        </Grid>
                    )}
                    <Grid item xs className={classes.dialogGridItem}>
                        <PrimaryButton
                            onClick={onYesSelected}
                            className={clsx(onNoSelected ? classes.dialogButton : classes.dialogButtonFullWidth, {
                                [yesButtonCustomStyle]: !!yesButtonCustomStyle,
                            })}
                            datatestid={datatestid ?? ''}
                            classes={yesButtonClasses ?? {}}
                            data-cy={dataCyYesButton ?? ''}
                        >
                            {yesButtonText}
                        </PrimaryButton>
                    </Grid>
                </Grid>
            </Box>
        </Dialog>
    );
};

/* type checking for props */
AlertModalYesNo.propTypes = {
    isDisplayed: PropTypes.bool.isRequired,
    alertModalTitle: PropTypes.string.isRequired,
    alertModalBody: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
    onNoSelected: PropTypes.func,
    onYesSelected: PropTypes.func.isRequired,
    onCloseSelected: PropTypes.func.isRequired,
    noButtonText: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    yesButtonText: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
};

export default AlertModalYesNo;
